import { PrevNext } from '@/components/CommonComponents/PrevNext';
import { Stepper } from '@/components/CommonComponents/Stepper';
import SuccessModal from '@/components/CommonComponents/SuccessModal';
import PageHeaderWrapper from '@/components/Layout/PageHeaderWrapper';
import { showToast } from '@/helper/toast.helper';
import { desanitizeKey } from '@/helper/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { AppDispatch, RootState } from '@/store';
import {
  createAdminUser,
  createBlueNumber,
  updateUser,
} from '@/store/slices/adminUsersSlice/adminUserSlice';
import * as libphonenumber from 'libphonenumber-js';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { parsePhoneNumber, validatePhoneNumber } from '../../../helper/phoneUtils';
import PersonalInformation from './PersonalInformation';
import ReviewAndCreate from './ReviewAndCreate';
import RoleAssignment from './RoleAssignment';

interface LoadingState {
  phone: boolean;
  blueNumber: boolean;
}

interface ErrorState {
  phone: string;
  blueNumber: string;
  welcomeMessage: string;
}

const CreateAdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  let editUser = location.state?.survey;
  const isEditMode = !!editUser;
  const [selectedPermissions, setSelectedPermissions] = useState<Record<string, string[]>>({});
  const [accessLevel, setAccessLevel] = useState<'full' | 'restricted' | 'readonly'>('full');
  const [settings, setSettings] = useState({
    mfa: false,
    autoTimeout: false,
    ipRestriction: false,
    auditLogging: false,
    timeRestriction: false,
  });
  const [maxUsers, setMaxUsers] = useState<number | ''>('');
  const [sessionDuration, setSessionDuration] = useState('60');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [step, setStep] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(editUser?.roleId || 0);
  const [welcomeMessage, setWelcomeMessage] = useState(editUser?.welcomeMsg || '');
  const [isValid, setIsValid] = useState<boolean>();
  const [isPhoneEdited, setIsPhoneEdited] = useState(false);

  const [loadingStates, setLoadingStates] = useState<LoadingState>({
    phone: false,
    blueNumber: false,
  });

  const [errorStates, setErrorStates] = useState<ErrorState>({
    phone: '',
    blueNumber: '',
    welcomeMessage: '',
  });

  const [form, setForm] = useState({
    blueNumberId: editUser?.blueNumberId || '',
    countryCode: editUser?.countryCode || '',
    phoneNumber: editUser?.countryCode + editUser?.phoneNumber || '',
    roleId: editUser?.roleId || 0,
    welcomeMsg: editUser?.welcomeMsg || '',
  });

  const roles = useSelector((state: RootState) => state.rolesPermissions?.filteredRoles || []);
  const { blueNumberData } = useSelector((state: RootState) => state.user);

  const selectedRoleData = roles.find((role: any) => role.id === selectedRole);
  const roleName = selectedRoleData?.name || 'N/A';
  const debouncedphone = useDebounce(form.phoneNumber, 500);
  const debouncedBlueNumber = useDebounce(form.blueNumberId, 500);
  const didInitEdit = React.useRef(false);
  const [isPhoneTouched, setIsPhoneTouched] = useState(false);

  const setLoading = (field: 'phone' | 'blueNumber', value: boolean) => {
    setLoadingStates((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const setErrorState = (field: 'phone' | 'blueNumber', message: string) => {
    setErrorStates((prev) => ({
      ...prev,
      [field]: message,
    }));
  };

  const clearErrorState = (field: 'phone' | 'blueNumber') => {
    setErrorState(field, '');
  };

  useEffect(() => {
    if (!isEditMode) return;
    if (didInitEdit.current) return;

    if (editUser?.phoneNumber && editUser?.blueNumberId) {
      setIsValid(true);
    }

    didInitEdit.current = true;
  }, [isEditMode, editUser]);

  useEffect(() => {
    if (blueNumberData?.blueNumber) {
      setForm((prev) => ({
        ...prev,
        blueNumberId: blueNumberData.blueNumber || '',
      }));
    } else if (!isEditMode && !isValid) {
      setForm((prev) => ({
        ...prev,
        blueNumberId: '',
      }));
    }
  }, [blueNumberData]);

  useEffect(() => {
    if (!isEditMode) {
      handleReset();
    }
  }, []);

  useEffect(() => {
    if (isEditMode && !isPhoneEdited) return;
    if (debouncedphone) {
      handlePhoneBlur(debouncedphone);
    }
  }, [debouncedphone]);

  const isNextDisabled = useMemo(() => {
    if (step === 0) {
      if (isEditMode && !isPhoneEdited) {
        return false;
      }

      return !isValid || !!errorStates.phone || !form.blueNumberId;
    }

    if (step === 1) {
      const messageLength = form.welcomeMsg.trim().length;
      const messageValid = messageLength > 10 && messageLength < 60;

      // Set error message for welcome message
      let welcomeMessageError = '';
      if (messageLength > 0 && messageLength < 10) {
        welcomeMessageError = 'The welcome message must be between 10 and 60 characters.';
      } else if (messageLength > 60) {
        welcomeMessageError = 'The welcome message must be between 10 and 60 characters.';
      }
      setErrorStates((prev) => ({ ...prev, welcomeMessage: welcomeMessageError }));

      return !form.roleId || !messageValid;
    }

    return false;
  }, [
    step,
    isValid,
    errorStates.phone,
    form.blueNumberId,
    form.roleId,
    form.welcomeMsg,
    isEditMode,
    isPhoneEdited,
  ]);

  const handlePrev = () => {
    setStep((prevStep) => Math.max(prevStep - 1, 0));
  };

  const handleNext = () => {
    setStep((prev) => Math.min(prev + 1, 2));
  };

  const handleReset = () => {
    setShowModal(false);

    // Reset ONLY step-0 fields
    if (step === 0) {
      if (isEditMode) {
        setForm((prev) => ({
          ...prev,
          blueNumberId: editUser?.blueNumberId,
          countryCode: editUser?.countryCode,
          phoneNumber: editUser?.countryCode + editUser?.phoneNumber,
        }));
      } else {
        setForm((prev) => ({
          ...prev,
          phoneNumber: '',
          blueNumberId: '',
        }));
      }
    }

    // Reset ONLY step-1 fields
    else if (step === 1) {
      if (isEditMode) {
        setForm((prev) => ({
          ...prev,
          roleId: editUser?.roleId,
          welcomeMsg: editUser?.welcomeMsg,
        }));
        setSelectedRole(editUser?.roleId);
        setWelcomeMessage(editUser?.welcomeMsg);
      } else {
        setForm((prev) => ({
          ...prev,
          roleId: 0,
          welcomeMsg: '',
        }));
        setSelectedRole(0);
        setWelcomeMessage('');
      }
    } else {
      setForm({
        phoneNumber: '',
        blueNumberId: '',
        countryCode: '',
        roleId: 0,
        welcomeMsg: '',
      });
      setSelectedRole(0);
      setWelcomeMessage('');
      setStep(0);
    }

    // Common resets you always want
    setSelectedPermissions({});
    setAccessLevel('full');
    setSettings({
      mfa: false,
      autoTimeout: false,
      ipRestriction: false,
      auditLogging: false,
      timeRestriction: false,
    });
    setMaxUsers('');
    setSessionDuration('60');
    setMaxUsers('');
    setSessionDuration('60');
    setErrorStates({ phone: '', blueNumber: '', welcomeMessage: '' });
  };

  const handleSave = async () => {
    try {
      const { countryCode, phoneNumber } = parsePhoneNumber(form.phoneNumber);
      const data = {
        ...form,
        countryCode: countryCode,
        phoneNumber: phoneNumber,
      };
      if (isEditMode) {
        await dispatch(updateUser({ id: editUser.id, payload: data })).unwrap();
        setShowModal(true);
      } else {
        await dispatch(createAdminUser(data)).unwrap();
        setShowModal(true);
      }
    } catch (error: any) {
      const message =
        (typeof error === 'string' && error) ||
        error?.message ||
        JSON.stringify(error) ||
        'Something went wrong';
      showToast.error(message);
    }
  };

  const setError = (field: 'phone' | 'blueNumber', message: string) => {
    setErrorStates((prev) => ({
      ...prev,
      [field]: message,
    }));
  };

  const clearError = (field: 'phone' | 'blueNumber') => {
    setError(field, '');
  };

  const handlePhoneBlur = async (phone: string) => {
    const trimmedPhone = phone.trim();

    if (!trimmedPhone) {
      setLoading('phone', false);
      return;
    }
    if (!trimmedPhone.startsWith('+')) {
      setError('phone', 'Phone number must start with country code (e.g., +1, +91)');
      setLoading('phone', false);
      return;
    }
    const { phoneNumber } = parsePhoneNumber(trimmedPhone);

    if (!isValid || !phoneNumber || phoneNumber.length < 5) {
      setError('phone', 'Invalid phone number');
      setLoading('phone', false);
      return;
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === 'blueNumberId') {
      clearErrorState('blueNumber');
    }
  };

  const handleMobileChange = async (value: string | undefined) => {
    try {
      setIsPhoneEdited(true);
      setIsPhoneTouched(true); // 👈 ADD THIS

      const newE164Value = value || '';
      const { countryCode, phoneNumber } = parsePhoneNumber(newE164Value);

      setForm((prev) => ({
        ...prev,
        countryCode,
        phoneNumber: newE164Value,
        blueNumberId: '',
      }));

      const { isValid } = validatePhoneNumber(phoneNumber, countryCode);

      setIsValid(isValid);

      if (!isValid) {
        setError('phone', 'Invalid phone number');
        return;
      }

      setLoading('phone', true);
      clearError('phone');

      if (libphonenumber.isValidPhoneNumber(newE164Value)) {
        await dispatch(createBlueNumber(newE164Value)).unwrap();
      }

      setLoading('phone', false);
    } catch {
      setError('phone', 'Invalid phone number');
      setLoading('phone', false);
    }
  };

  return (
    <PageHeaderWrapper
      title={'System Admin Users'}
      description="Manage administrator accounts and permissions."
      onAdd={() => navigate(-1)}
      addLabel=" Return to Admin User List"
    >
      <div className="">
        <Stepper
          steps={[
            {
              title: 'Personal Information',
              subtitle: 'Enter admin details',
            },
            {
              title: 'Admin Role Assignment',
              subtitle: 'Choose the role for this user',
            },
            {
              title: isEditMode ? 'Review & Update' : 'Review & Create',
              subtitle: 'Final check & confirmation ',
            },
          ]}
          activeStep={step}
        />
      </div>

      <div className="bg-white rounded-xl">
        <div className={step === 0 ? 'block' : 'hidden'}>
          <PersonalInformation
            form={form}
            handleChange={handleChange}
            handleMobileChange={handleMobileChange}
            loadingStates={loadingStates}
            errorStates={errorStates}
            isEditMode={isEditMode}
          />
        </div>
        <div className={step === 1 ? 'block' : 'hidden'}>
          <RoleAssignment
            selectedRole={form.roleId}
            setSelectedRole={(value) => {
              setSelectedRole(value);
              setForm((prev) => ({ ...prev, roleId: value }));
            }}
            welcomeMessage={form.welcomeMsg}
            setWelcomeMessage={(value) => {
              setForm((prev) => ({ ...prev, welcomeMsg: value }));
            }}
            welcomeMessageError={errorStates.welcomeMessage}
            isEditMode={isEditMode}
          />
        </div>
        <div className={step === 2 ? 'block' : 'hidden'}>
          <ReviewAndCreate
            form={form}
            selectedPermissions={selectedPermissions}
            accessLevel={accessLevel}
            settings={settings}
            maxUsers={maxUsers}
            sessionDuration={sessionDuration}
            isConfirmed={isConfirmed}
            setIsConfirmed={setIsConfirmed}
            selectedRole={selectedRole}
            welcomeMessage={welcomeMessage}
            isEditedMode={isEditMode}
          />
        </div>

        <div className="p-6">
          <PrevNext
            hidePrevious={step === 0}
            prev={handlePrev}
            next={handleNext}
            onReset={handleReset}
            disableReset={step === 0 || step === 2}
            disableCreate={!isConfirmed}
            disableNext={isNextDisabled}
            create={step === 2}
            onCreate={handleSave}
            createLabel={isEditMode ? 'Update Admin User →' : 'Create New Admin User →'}
          />
        </div>
      </div>
      {/* Success Modal */}
      <SuccessModal
        isOpen={showModal}
        title={isEditMode ? 'Admin User Updated Successfully!' : 'Admin User Created Successfully!'}
        dataFields={[{ label: 'Role', value: desanitizeKey(roleName.toString()) || 'N/A' }]}
        actions={['Role-based access granted', 'Audit log entry recorded']}
        onClose={() => setShowModal(false)}
        primaryButtonText={!isEditMode ? 'Create Another Admin' : undefined}
        actionsHeader={isEditMode ? 'Post-Update Actions' : 'Post-Creation Actions'}
        onPrimaryAction={!isEditMode ? handleReset : undefined}
        onSecondaryAction={() => {
          setShowModal(false);
          navigate('/dashboard/system-admin-users');
        }}
        secondaryButtonText="View Admin List"
      />
    </PageHeaderWrapper>
  );
};

export default CreateAdminUsers;
